import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import axios from 'axios';

const Container = styled.div<{ $embedded?: boolean }>`
  max-width: 1800px;
  margin: 0 auto;
  padding: ${props => props.$embedded ? '1rem' : '2rem'};
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 2rem;
`;

const Title = styled.h2`
  color: white;
  font-size: 1.5rem;
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 0.75rem;
`;

const Actions = styled.div`
  display: flex;
  gap: 1rem;
`;

const Button = styled.button<{ $variant?: 'primary' | 'secondary' }>`
  padding: 0.75rem 1.5rem;
  border-radius: 8px;
  font-weight: 600;
  font-size: 0.875rem;
  cursor: pointer;
  transition: all 0.2s;
  display: flex;
  align-items: center;
  gap: 0.5rem;

  ${props => props.$variant === 'primary' ? `
    background: linear-gradient(135deg, #06b6d4 0%, #0891b2 100%);
    color: white;
    border: none;
    &:hover { transform: translateY(-1px); box-shadow: 0 4px 12px rgba(6, 182, 212, 0.4); }
  ` : `
    background: transparent;
    color: #94a3b8;
    border: 1px solid rgba(255, 255, 255, 0.1);
    &:hover { border-color: #06b6d4; color: white; }
  `}
`;

const StatsRow = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
  gap: 1rem;
  margin-bottom: 2rem;
`;

const StatCard = styled.div<{ $color?: string }>`
  background: linear-gradient(135deg, rgba(30, 41, 59, 0.9) 0%, rgba(51, 65, 85, 0.9) 100%);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 12px;
  padding: 1.25rem;
  border-left: 4px solid ${props => props.$color || '#06b6d4'};
`;

const StatLabel = styled.div`
  color: #94a3b8;
  font-size: 0.75rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  margin-bottom: 0.25rem;
`;

const StatValue = styled.div`
  color: white;
  font-size: 1.5rem;
  font-weight: 700;
`;

const TabsRow = styled.div`
  display: flex;
  gap: 0.5rem;
  margin-bottom: 1.5rem;
`;

const TabButton = styled.button<{ $active: boolean }>`
  padding: 0.5rem 1rem;
  border-radius: 8px;
  font-weight: 600;
  font-size: 0.875rem;
  cursor: pointer;
  transition: all 0.2s;
  background: ${props => props.$active ? 'rgba(6, 182, 212, 0.2)' : 'transparent'};
  border: 1px solid ${props => props.$active ? '#06b6d4' : 'rgba(255, 255, 255, 0.1)'};
  color: ${props => props.$active ? 'white' : '#94a3b8'};

  &:hover {
    border-color: #06b6d4;
    color: white;
  }
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  background: rgba(30, 41, 59, 0.5);
  border-radius: 12px;
  overflow: hidden;
`;

const Th = styled.th`
  padding: 1rem;
  text-align: left;
  background: rgba(30, 41, 59, 0.8);
  color: #94a3b8;
  font-weight: 600;
  font-size: 0.75rem;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
`;

const Td = styled.td`
  padding: 1rem;
  color: white;
  font-size: 0.875rem;
  border-bottom: 1px solid rgba(255, 255, 255, 0.05);
`;

const TypeBadge = styled.span<{ $type: string }>`
  padding: 4px 10px;
  border-radius: 6px;
  font-size: 0.7rem;
  font-weight: 700;
  text-transform: uppercase;

  ${props => {
    switch (props.$type) {
      case 'DEALER':
        return 'background: rgba(59, 130, 246, 0.1); color: #3b82f6; border: 1px solid rgba(59, 130, 246, 0.2);';
      case 'BANK':
        return 'background: rgba(16, 185, 129, 0.1); color: #10b981; border: 1px solid rgba(16, 185, 129, 0.2);';
      case 'HEDGE_FUND':
        return 'background: rgba(139, 92, 246, 0.1); color: #8b5cf6; border: 1px solid rgba(139, 92, 246, 0.2);';
      case 'ASSET_MANAGER':
        return 'background: rgba(245, 158, 11, 0.1); color: #f59e0b; border: 1px solid rgba(245, 158, 11, 0.2);';
      default:
        return 'background: rgba(148, 163, 184, 0.1); color: #94a3b8; border: 1px solid rgba(148, 163, 184, 0.2);';
    }
  }}
`;

const CreditUtilization = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
`;

const UtilizationBar = styled.div`
  width: 80px;
  height: 8px;
  background: rgba(255, 255, 255, 0.1);
  border-radius: 4px;
  overflow: hidden;
`;

const UtilizationFill = styled.div<{ $percent: number }>`
  height: 100%;
  width: ${props => props.$percent}%;
  background: ${props => {
    if (props.$percent >= 90) return '#ef4444';
    if (props.$percent >= 75) return '#f59e0b';
    return '#10b981';
  }};
  border-radius: 4px;
`;

const UtilizationText = styled.span<{ $percent: number }>`
  font-size: 0.8rem;
  font-weight: 600;
  color: ${props => {
    if (props.$percent >= 90) return '#ef4444';
    if (props.$percent >= 75) return '#f59e0b';
    return '#10b981';
  }};
`;

const ISDABadge = styled.span<{ $active: boolean }>`
  padding: 4px 8px;
  border-radius: 4px;
  font-size: 0.7rem;
  font-weight: 600;
  background: ${props => props.$active ? 'rgba(16, 185, 129, 0.1)' : 'rgba(239, 68, 68, 0.1)'};
  color: ${props => props.$active ? '#10b981' : '#ef4444'};
`;

interface Counterparty {
  id: number;
  counterpartyName: string;
  counterpartyCode: string;
  counterpartyType: string;
  lei: string;
  creditLimit: number;
  creditLimitUsed: number;
  hasIsda: boolean;
  isdaVersion: string;
  csaThreshold: number;
  mta: number;
  isActive: boolean;
  primaryContact: string;
  primaryEmail: string;
}

interface CounterpartyManagementScreenProps {
  embedded?: boolean;
}

const CounterpartyManagementScreen: React.FC<CounterpartyManagementScreenProps> = ({ embedded = false }) => {
  const [counterparties, setCounterparties] = useState<Counterparty[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [activeTab, setActiveTab] = useState<'all' | 'dealers' | 'banks' | 'near-limit'>('all');

  useEffect(() => {
    const fetchCounterparties = async () => {
      try {
        setLoading(true);
        const response = await axios.get('/api/credit/counterparties');
        setCounterparties(response.data);
      } catch (error) {
        console.error('Failed to fetch counterparties:', error);
        // A failed load must render as a load error, never as a plausible book.
        setCounterparties([]);
        setLoadError(true);
      } finally {
        setLoading(false);
      }
    };

    fetchCounterparties();
  }, []);

  const formatCurrency = (value: number) => {
    if (value == null) return '$0';
    if (value >= 1000000000) return `$${(value / 1000000000).toFixed(2)}B`;
    if (value >= 1000000) return `$${(value / 1000000).toFixed(0)}M`;
    return `$${value.toLocaleString()}`;
  };

  const getUtilization = (cp: Counterparty) => Math.round((cp.creditLimitUsed / cp.creditLimit) * 100);

  const filteredCounterparties = counterparties.filter(cp => {
    if (activeTab === 'all') return true;
    if (activeTab === 'dealers') return cp.counterpartyType === 'DEALER';
    if (activeTab === 'banks') return cp.counterpartyType === 'BANK';
    if (activeTab === 'near-limit') return getUtilization(cp) >= 80;
    return true;
  });

  const totalLimit = counterparties.reduce((sum, cp) => sum + cp.creditLimit, 0);
  const totalUsed = counterparties.reduce((sum, cp) => sum + cp.creditLimitUsed, 0);
  const nearLimitCount = counterparties.filter(cp => getUtilization(cp) >= 80).length;
  const dealerCount = counterparties.filter(cp => cp.counterpartyType === 'DEALER').length;

  return (
    <Container $embedded={embedded}>
      {!embedded && (
        <Header>
          <Title>
            <span>🤝</span>
            Counterparty & ISDA Management
          </Title>
          <Actions>
            <Button $variant="secondary">Export</Button>
            <Button $variant="primary">+ New Counterparty</Button>
          </Actions>
        </Header>
      )}

      {loadError && (
        <div style={{ margin: '0 0 1rem', padding: '0.6rem 0.9rem', borderRadius: 8, border: '1px solid rgba(239,68,68,0.35)', background: 'rgba(239,68,68,0.1)', color: '#fca5a5', fontSize: '0.8rem' }}>
          ⚠ Couldn't load counterparties — the request failed. This is a load error, not an empty book.
        </div>
      )}

      <StatsRow>
        <StatCard $color="#06b6d4">
          <StatLabel>Total Limit</StatLabel>
          <StatValue>{formatCurrency(totalLimit)}</StatValue>
        </StatCard>
        <StatCard $color="#10b981">
          <StatLabel>Total Used</StatLabel>
          <StatValue>{formatCurrency(totalUsed)}</StatValue>
        </StatCard>
        <StatCard $color="#3b82f6">
          <StatLabel>Counterparties</StatLabel>
          <StatValue>{counterparties.length}</StatValue>
        </StatCard>
        <StatCard $color="#f59e0b">
          <StatLabel>Dealers</StatLabel>
          <StatValue>{dealerCount}</StatValue>
        </StatCard>
        <StatCard $color="#ef4444">
          <StatLabel>Near Limit</StatLabel>
          <StatValue>{nearLimitCount}</StatValue>
        </StatCard>
      </StatsRow>

      <TabsRow>
        <TabButton $active={activeTab === 'all'} onClick={() => setActiveTab('all')}>
          All ({counterparties.length})
        </TabButton>
        <TabButton $active={activeTab === 'dealers'} onClick={() => setActiveTab('dealers')}>
          Dealers ({counterparties.filter(cp => cp.counterpartyType === 'DEALER').length})
        </TabButton>
        <TabButton $active={activeTab === 'banks'} onClick={() => setActiveTab('banks')}>
          Banks ({counterparties.filter(cp => cp.counterpartyType === 'BANK').length})
        </TabButton>
        <TabButton $active={activeTab === 'near-limit'} onClick={() => setActiveTab('near-limit')}>
          Near Limit ({nearLimitCount})
        </TabButton>
      </TabsRow>

      <Table>
        <thead>
          <tr>
            <Th>Counterparty</Th>
            <Th>Type</Th>
            <Th>Credit Limit</Th>
            <Th>Utilization</Th>
            <Th>ISDA</Th>
            <Th>CSA Threshold</Th>
            <Th>MTA</Th>
            <Th>Contact</Th>
          </tr>
        </thead>
        <tbody>
          {filteredCounterparties.map(cp => {
            const utilization = getUtilization(cp);
            return (
              <tr key={cp.id}>
                <Td>
                  <div style={{ fontWeight: 600 }}>{cp.counterpartyName}</div>
                  <div style={{ fontSize: '0.75rem', color: '#64748b' }}>{cp.lei}</div>
                </Td>
                <Td><TypeBadge $type={cp.counterpartyType}>{cp.counterpartyType.replace(/_/g, ' ')}</TypeBadge></Td>
                <Td>{formatCurrency(cp.creditLimit)}</Td>
                <Td>
                  <CreditUtilization>
                    <UtilizationBar>
                      <UtilizationFill $percent={utilization} />
                    </UtilizationBar>
                    <UtilizationText $percent={utilization}>{utilization}%</UtilizationText>
                  </CreditUtilization>
                </Td>
                <Td>
                  <ISDABadge $active={cp.hasIsda}>
                    {cp.hasIsda ? `ISDA ${cp.isdaVersion}` : 'No ISDA'}
                  </ISDABadge>
                </Td>
                <Td>{formatCurrency(cp.csaThreshold)}</Td>
                <Td>{formatCurrency(cp.mta)}</Td>
                <Td>
                  <div style={{ fontSize: '0.85rem' }}>{cp.primaryContact}</div>
                  <div style={{ fontSize: '0.75rem', color: '#64748b' }}>{cp.primaryEmail}</div>
                </Td>
              </tr>
            );
          })}
        </tbody>
      </Table>
    </Container>
  );
};

export default CounterpartyManagementScreen;
